import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Card,
  CardContent,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { mockParents, mockStudents, mockCommunityMembers } from '../../mock';
import { Parent, Student, CommunityMember } from '../../types';

export function AccountSettings() {
  const { user, parent, student, communityMember } = useAuth();
  const [formData, setFormData] = useState<Partial<Parent | Student | CommunityMember>>({});
  const [originalFormData, setOriginalFormData] = useState<Partial<Parent | Student | CommunityMember>>({});
  const [savedMessage, setSavedMessage] = useState(false);
  const [relatedStudents, setRelatedStudents] = useState<Student[]>([]);
  const [originalStudentIds, setOriginalStudentIds] = useState<string[]>([]);
  const [addStudentDialogOpen, setAddStudentDialogOpen] = useState(false);
  const [newStudentId, setNewStudentId] = useState('');
  const [addStudentError, setAddStudentError] = useState('');

  useEffect(() => {
    if (parent) {
      const parentData = mockParents.find(p => p.id === parent.id);
      if (parentData) {
        setFormData(parentData);
        setOriginalFormData(parentData);
        const students = mockStudents.filter(s => parent.studentIds.includes(s.id));
        setRelatedStudents(students);
        setOriginalStudentIds(students.map(s => s.id));
      }
    } else if (student) {
      const studentData = mockStudents.find(s => s.id === student.id);
      if (studentData) {
        setFormData(studentData);
        setOriginalFormData(studentData);
      }
    } else if (communityMember) {
      const memberData = mockCommunityMembers.find(c => c.id === communityMember.id);
      if (memberData) {
        setFormData(memberData);
        setOriginalFormData(memberData);
      }
    }
  }, [parent, student, communityMember]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setOriginalFormData(formData);
    setOriginalStudentIds(relatedStudents.map(s => s.id));
    setSavedMessage(true);
    setTimeout(() => setSavedMessage(false), 3000);
  };

  const handleAddStudent = () => {
    setAddStudentError('');
    const foundStudent = mockStudents.find(s => s.studentIdNumber === newStudentId);

    if (!foundStudent) {
      setAddStudentError('Student ID not found');
      return;
    }

    if (relatedStudents.some(s => s.id === foundStudent.id)) {
      setAddStudentError('Student is already linked to this account');
      return;
    }

    setRelatedStudents(prev => [...prev, foundStudent]);
    setAddStudentDialogOpen(false);
    setNewStudentId('');
  };

  const handleRemoveStudent = (studentId: string) => {
    setRelatedStudents(prev => prev.filter(s => s.id !== studentId));
  };

  const handleOpenAddDialog = () => {
    setAddStudentDialogOpen(true);
    setNewStudentId('');
    setAddStudentError('');
  };

  const handleCloseAddDialog = () => {
    setAddStudentDialogOpen(false);
    setNewStudentId('');
    setAddStudentError('');
  };

  const hasChanges = useMemo(() => {
    const formFields = ['firstName', 'lastName', 'phone', 'address', 'city', 'state', 'zipCode'];
    const hasFormChanges = formFields.some(field => formData[field] !== originalFormData[field]);

    if (parent) {
      const currentStudentIds = relatedStudents.map(s => s.id).sort();
      const originalIds = originalStudentIds.slice().sort();
      const hasStudentChanges = currentStudentIds.length !== originalIds.length ||
        currentStudentIds.some((id, index) => id !== originalIds[index]);
      return hasFormChanges || hasStudentChanges;
    }

    return hasFormChanges;
  }, [formData, originalFormData, relatedStudents, originalStudentIds, parent]);

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Please log in to view your account settings.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <PersonIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight={700}>
            Account Settings
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage your personal information and preferences
        </Typography>
      </Box>

      {savedMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Your changes have been saved successfully!
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Personal Information
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.firstName || ''}
              onChange={(e) => handleChange('firstName', e.target.value)}
              disabled={!!student}
              helperText={student ? "Name cannot be changed" : undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData.lastName || ''}
              onChange={(e) => handleChange('lastName', e.target.value)}
              disabled={!!student}
              helperText={student ? "Name cannot be changed" : undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Email Address"
              value={formData.email || ''}
              disabled
              helperText="Email address cannot be changed"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Address
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Street Address"
              value={formData.address || ''}
              onChange={(e) => handleChange('address', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="City"
              value={formData.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="State"
              value={formData.state || ''}
              onChange={(e) => handleChange('state', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="ZIP Code"
              value={formData.zipCode || ''}
              onChange={(e) => handleChange('zipCode', e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {parent && (
        <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Student Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View students linked to your account
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Add Student
            </Button>
          </Box>
          <Divider sx={{ my: 2 }} />

          {relatedStudents.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No students linked to this account. Click "Add Student" to link a student by their Student ID.
            </Typography>
          ) : (
            relatedStudents.map((studentData, index) => (
              <Card
                key={studentData.id}
                elevation={0}
                sx={{
                  mb: index < relatedStudents.length - 1 ? 3 : 0,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {studentData.firstName} {studentData.lastName}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveStudent(studentData.id)}
                      title="Remove student from account"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="First Name"
                        value={studentData.firstName}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Last Name"
                        value={studentData.lastName}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Email"
                        value={studentData.email}
                        disabled
                        helperText="Email address cannot be changed"
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Grade"
                        value={`Grade ${studentData.grade}`}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Student ID"
                        value={studentData.studentIdNumber}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Phone Number"
                        value={studentData.phone || 'Not provided'}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!hasChanges}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Save Changes
        </Button>
      </Box>

      <Dialog open={addStudentDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Student to Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter the Student ID number to link a student to your account.
          </Typography>
          {addStudentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addStudentError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Student ID Number"
            placeholder="e.g., S2024001"
            value={newStudentId}
            onChange={(e) => setNewStudentId(e.target.value)}
            autoFocus
            helperText="You can find this on the student's enrollment documents"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddStudent}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Add Student
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
